import { brandFor, type Brand } from './brands'

export type Tone = 'success' | 'failed' | 'running' | 'info' | 'neutral'

/** How one label/value pair of a lifecycle view is shown. The text itself is never changed or dropped. */
export type Presented =
  | { kind: 'empty'; text: string }
  | { kind: 'status'; text: string; tone: Tone }
  | { kind: 'link'; text: string; href: string }
  | { kind: 'email'; text: string }
  | { kind: 'flag'; text: string; on: boolean | null }
  | { kind: 'chips'; items: Array<{ text: string; brand: Brand | null }> }
  | { kind: 'brand'; text: string; brand: Brand }
  | { kind: 'code'; text: string; icon: 'branch' | 'commit' | null }
  | { kind: 'metric'; text: string }
  | { kind: 'text'; text: string }

const emptyValue =
  /^(not (provided|available|generated|run|measured|analy[sz]ed|reported|selected|started)|no (scan findings available|commits?|reviews?|comments?)|none( selected)?( saved)?|unavailable|—|-)$/i

const statusLabel =
  /status|quality gate|latest decision|architect review|consumer review|validation result|pull request state/i

const listLabels = new Set([
  'resiliency',
  'observability',
  'test categories',
  'clients',
  'consumers',
  'linked consumers',
  'review teams',
  'approved by',
  'saved sections',
  'deployment targets',
  'pipeline environments',
  'operations',
  'servers',
  'checks',
])

const brandLabels = new Set([
  'framework',
  'environment',
  'build tool',
  'java version',
  'language',
  'provider',
  'source provider',
  'cloud provider',
  'deployment target',
  'strategy type',
])

const codeLabels: Record<string, 'branch' | 'commit' | null> = {
  branch: 'branch',
  'source branch': 'branch',
  'target branch': 'branch',
  'head commit': 'commit',
  commit: 'commit',
  pipeline: null,
  'merge commit': 'commit',
  repository: null,
  'repository owner': null,
  'resource id': null,
  'application id': null,
  'created by id': null,
  package: null,
  artifact: null,
  group: null,
  'base path': null,
  host: null,
  port: null,
  'scan target': null,
}

const flagWords: Record<string, boolean | null> = {
  yes: true,
  no: false,
  enabled: true,
  disabled: false,
  configured: true,
  'not configured': false,
  true: true,
  false: false,
}

/** The tone of a state word such as DEPLOYED, FAILED, PENDING_REVIEW, IN_PROGRESS. */
export function statusTone(value: string): Tone {
  const state = value
    .trim()
    .toUpperCase()
    .replaceAll(/[\s-]+/g, '_')
  if (
    [
      'DEPLOYED',
      'HEALTHY',
      'CONNECTED',
      'COMPLETED',
      'COMPLETE',
      'SUCCESS',
      'SUCCEEDED',
      'PASSED',
      'APPROVED',
      'MERGED',
      'READY',
      'ACTIVE',
      'GENERATED',
      'VALID',
      'AVAILABLE',
      'READY_TO_MERGE',
    ].includes(state)
  )
    return 'success'
  if (
    [
      'FAILED',
      'FAILURE',
      'ERROR',
      'REJECTED',
      'CHANGES_REQUESTED',
      'BLOCKED',
      'CLOSED',
      'MERGE_FAILED',
      'INVALID',
      'CANCELLED',
    ].includes(state)
  )
    return 'failed'
  if (
    [
      'IN_PROGRESS',
      'RUNNING',
      'QUEUED',
      'MERGING',
      'WAITING_EXECUTION',
      'GENERATING',
      'WARNING',
      'PARTIAL',
    ].includes(state)
  )
    return 'running'
  if (
    [
      'PENDING',
      'PENDING_REVIEW',
      'OPEN',
      'SENT',
      'REQUESTED',
      'REVIEW_REQUESTED',
    ].includes(state)
  )
    return 'info'
  return 'neutral'
}

export function presentField(field: {
  label: string
  value: string
}): Presented {
  const { label } = field
  const text = field.value
  const key = label.trim().toLowerCase()
  const value = text.trim()

  if (!value || emptyValue.test(value))
    return { kind: 'empty', text: value || 'Not provided' }

  if (/^https?:\/\/\S+$/i.test(value))
    return { kind: 'link', text: value, href: value }
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
    return { kind: 'email', text: value }

  if (
    statusLabel.test(label) &&
    !/^\d/.test(value) &&
    !flagWords[value.toLowerCase()]
  )
    return { kind: 'status', text: value, tone: statusTone(value) }

  const flag = flagWords[value.toLowerCase()]
  if (flag !== undefined) return { kind: 'flag', text: value, on: flag }

  if (listLabels.has(key) && /,/.test(value))
    return {
      kind: 'chips',
      items: value
        .split(/\s*,\s*/)
        .filter(Boolean)
        .map((item) => ({ text: item, brand: brandFor(item) })),
    }

  if (brandLabels.has(key) || (key === 'type' && brandFor(value))) {
    // a Java version is just a number ("17"): the logo is the language's
    const brand = key === 'java version' ? brandFor('Java') : brandFor(value)
    if (brand) return { kind: 'brand', text: value, brand }
  }

  if (key in codeLabels)
    return { kind: 'code', text: value, icon: codeLabels[key] ?? null }

  if (/^-?\d+(\.\d+)?\s?(%|ms|s|files|lines|min|h)?$/i.test(value))
    return { kind: 'metric', text: value }

  return { kind: 'text', text: value }
}
